import type {
  ClassProvider,
  ConstructorProvider,
  ExistingProvider,
  FactoryProvider,
  InjectOptions,
  ProviderToken,
  Type,
  ValueProvider,
} from '@angular/core';
import { inject, InjectionToken } from '@angular/core';

export type Providers =
  | ValueProvider
  | ClassProvider
  | ConstructorProvider
  | ExistingProvider
  | FactoryProvider;

type TypedProvider<T> =
  | { useValue: T }
  | { useClass: Type<T> }
  | { useExisting: ProviderToken<T> }
  | { useFactory: (...args: any[]) => T };

type InjectionTokenOptions<T, Multi extends boolean> = {
  providedIn?: Type<any> | 'root' | 'platform' | 'any' | null;
  factory: () => Multi extends true ? T[] : T;
};

type Nullable<T> = T | null;
type ArrayOrSingle<T, Multi extends boolean> = Multi extends true ? T[] : T;

type ResolveInjectReturn<
  T,
  Multi extends boolean,
  TokenOpts extends InjectionTokenOptions<T, any> | undefined,
  InjectOpts extends InjectOptions,
> = TokenOpts extends { factory: () => infer R }
  ? R
  : InjectOpts extends { optional: true }
    ? Nullable<ArrayOrSingle<T, Multi>>
    : ArrayOrSingle<T, Multi>;

/**
 * @desc Typed wrapper around Angular Provider
 * Usage:
 * provide(COMPANY_ID, { useValue: 1 });
 * provide(COMPANY_ID, { useFactory: () => 2, multi: true });
 */
export const provide = <
  PToken extends ProviderToken<any>,
  ProvideType extends TypedProvider<PToken extends ProviderToken<infer R> ? R : never> & {
    multi?: Multi;
  },
  Multi extends boolean,
>(
  token: PToken,
  provideType: ProvideType,
) =>
  <
    Multi extends true
      ? { provide: PToken } & ProvideType & { multi: true }
      : { provide: PToken } & ProvideType
  >{
    ...provideType,
    provide: token,
  };

/**
 * @desc Create InjectionToken with inject/provide pair
 * Usage:
 * export const { provide: provideCompanyId, inject: injectCompanyId } = createProviderToken<number>('COMPANY_ID')(false);
 * Provide
 *  provideCompanyId({ useFactory: () => 1 }),
 * Inject
 *   public readonly companyId = injectCompanyId();
 */
export const createProviderToken =
  <T>(name: string) =>
  <TokenOpts extends InjectionTokenOptions<T, Multi> | undefined, Multi extends boolean = false>(
    multi?: Multi,
    injectionTokenOptions?: TokenOpts,
  ) => {
    const token = new InjectionToken<T>(
      name,
      <InjectionTokenOptions<T, false>>injectionTokenOptions,
    );
    return {
      provide: <U extends TypedProvider<T>>(provideType: U) => {
        const options = <U & { multi: Multi }>{ ...provideType, multi };
        return provide<InjectionToken<T>, U, Multi>(token, options);
      },
      inject: <InjectOpts extends InjectOptions>(injectOptions?: InjectOpts) =>
        <ResolveInjectReturn<T, Multi, TokenOpts, InjectOpts>>inject(token, injectOptions ?? {}),
    };
  };

/**
 * @desc Create inject/provide pair for ProviderToken (class or InjectionToken)
 * Usage:
 * export const { provide: provideAsyncJob, inject: injectAsyncJob } = createProviderClass(AsyncJobConfig)(true);
 * Provide
 *  [
 *    provideAsyncJob({ useClass: CandidateMergeAsyncJob }),
 *    provideAsyncJob({ useClass: ImportRevertAsyncJob }),
 *    provideAsyncJob({ useClass: MailboxSendAsyncJob }),
 *  ]
 *  Inject
 *  public readonly asyncJobConfigs = injectAsyncJob(); // is AsyncJobConfig[]
 */
export const createProviderClass =
  <T>(klass: ProviderToken<T>) =>
  <Multi extends boolean = false>(multi?: Multi) => ({
    provide: <U extends TypedProvider<T>>(provideType: U) =>
      provide(klass, { ...provideType, multi }),
    inject: <InjectOpts extends InjectOptions>(injectOptions?: InjectOpts) =>
      <ResolveInjectReturn<T, Multi, undefined, InjectOpts>>inject(klass, injectOptions ?? {}),
  });
